import React, { useState } from 'react';
import { Mail, Phone, Instagram, Send, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';

const ContactUs = () => {
  const { t } = useLocale();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      toast({ title: t('contactFillRequired') });
      return;
    }
    setSending(true);
    // No mail backend yet — open the user's mail client with a pre-filled draft
    const subject = encodeURIComponent(form.subject || 'Feron Store enquiry');
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:feronstore@web.de?subject=${subject}&body=${body}`;
    setTimeout(() => setSending(false), 800);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-14" data-testid="contact-us-page">
      <section className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">{t('contactTitle')}</h1>
        <div className="h-1 w-16 bg-[#22c55e] mx-auto mt-3" />
        <p className="text-gray-600 mt-4 max-w-xl mx-auto">{t('contactSubtitle')}</p>
      </section>

      <div className="grid md:grid-cols-[300px_1fr] gap-10">
        {/* Contact channels */}
        <aside className="space-y-5">
          <div className="p-5 bg-neutral-50 border rounded-xl">
            <Mail size={20} className="text-[#22c55e] mb-2" />
            <div className="font-bold text-sm">{t('contactEmail')}</div>
            <a href="mailto:feronstore@web.de" className="text-sm text-gray-700 hover:text-[#22c55e] break-all">feronstore@web.de</a>
          </div>
          <div className="p-5 bg-neutral-50 border rounded-xl">
            <Phone size={20} className="text-[#22c55e] mb-2" />
            <div className="font-bold text-sm">{t('contactPhone')}</div>
            <a href="tel:+919769312170" className="text-sm text-gray-700 hover:text-[#22c55e]">+91 97693 12170</a>
          </div>
          <div className="p-5 bg-neutral-50 border rounded-xl">
            <Instagram size={20} className="text-[#22c55e] mb-2" />
            <div className="font-bold text-sm">Instagram</div>
            <a href="https://www.instagram.com/feron._store" target="_blank" rel="noreferrer noopener" className="text-sm text-gray-700 hover:text-[#22c55e]">@feron._store</a>
          </div>
          <a
            href="https://wa.me/919769312170?text=Hi%20Feron%20Store%2C%20I%20have%20a%20question"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="contact-whatsapp-btn"
            className="p-5 bg-[#25D366] hover:bg-[#1ebe5b] transition-colors rounded-xl flex items-center gap-3 group"
          >
            <MessageCircle size={22} className="text-white shrink-0" />
            <div>
              <div className="font-bold text-sm text-white">Chat on WhatsApp</div>
              <div className="text-xs text-white/90">+91 97693 12170 · tap to message us</div>
            </div>
          </a>
        </aside>

        {/* Form */}
        <form onSubmit={submit} className="bg-white border rounded-2xl p-6 md:p-8 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-700">{t('contactName')}</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">{t('contactYourEmail')} *</label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">{t('contactSubject')}</label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">{t('contactMessage')} *</label>
            <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-[#22c55e]" data-testid="contact-message" />
          </div>
          <Button type="submit" disabled={sending} className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-black font-black py-6 tracking-wider" data-testid="contact-submit">
            <Send size={16} className="mr-2" />
            {sending ? '...' : t('contactSend')}
          </Button>
          <p className="text-[11px] text-gray-500">{t('contactMailtoNote')}</p>
        </form>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactUs;
